import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, Input, Select, Form, Tooltip, Spin, Collapse } from "antd";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { getRequest, postRequest } from "../../axios";
import { authUserData } from "../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import PropTypes, { bool } from "prop-types";
import { InfoCircleFilled } from "@ant-design/icons";

const { Panel } = Collapse;
const { Option } = Select;

export const SPECIFIC_GRAVITY = {
  GI: 7.85,
  MS: 7.85,
  CU: 8.96,
  AL: 2.7,
  SS: 8.0,
};

export function getKgPerMeter(widthMM, thicknessMM, material) {
  const sg = SPECIFIC_GRAVITY[material.toUpperCase()] || 0;
  if (sg === 0) return 0;
  const density = sg * 1000; // kg/m³
  const widthM = widthMM / 1000;
  const thicknessM = thicknessMM / 1000;
  return widthM * thicknessM * 1 * density; // per meter
}

const parseEarthingStrip = (name) => {
  const match = name.match(/(\d+)\s*x\s*(\d+)\s*[-–]\s*(\w+)/i);
  if (!match) return null;
  return {
    width: parseFloat(match[1]),
    thickness: parseFloat(match[2]),
    material: match[3].trim().toUpperCase(),
  };
};

const NewAddPRMaterial = ({ userSelectedSiteList, selectedCategoryName }) => {
  const [siteName, setSiteName] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id, prId } = useParams();
  const { state: details } = useLocation();
  const { categoryId, siteIds } = details;
  const CompanyID = authUserData()?.company ? authUserData()?.company : null;
  const [pincodeList, setPincodeList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estimatedValueFromApi, setEstimatedValueFromApi] = useState([
    { id: "", value: "" },
  ]);
  const [totalAmountPr, setTotalAmountPr] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const [currencySymbol, setCurrencySymbol] = useState("₹");

  const initialState = {
    company: CompanyID,
    category_tree: id,
    total_amount: null,
    purchase_requisition_details: [],
  };
  const [state, setState] = useState(initialState);

  console.log("selectedCategoryName", selectedCategoryName);

  useEffect(() => {
    if (categoryId && siteIds) {
      getSubCategory();
      getCurrency();
    }
  }, [categoryId, siteIds]);

  useEffect(() => {
    if (userSelectedSiteList.userSelectedSiteList?.length > 0) {
      setSiteName(userSelectedSiteList.userSelectedSiteList);
    } else if (siteName.length === 0) {
      getSiteName();
    }
    getUnitOfMeasurement();
  }, [userSelectedSiteList]);

  useEffect(() => {
    const calculateTotalAmounts = () => {
      const amounts = state.purchase_requisition_details.map((detail) => {
        const matchedEstimation = estimatedValueFromApi.find(
          (est) => est.id === detail.material_type_id
        );

        if (
          matchedEstimation &&
          typeof detail.total_quantity === "number" &&
          typeof matchedEstimation.value === "number"
        ) {
          if (detail.unit_of_measurement_name === "Cubic Meter") {
            return detail.total_quantity * matchedEstimation.value;
          } else {
            return detail.total_quantity_by_kg * detail.estimated_price;
          }
        } else {
          return 0;
        }
      });

      const totalAmount = amounts.reduce((acc, curr) => acc + curr, 0);
      setTotalAmountPr(totalAmount);

      setState((prevState) => ({
        ...prevState,
        total_amount: totalAmount,
      }));
    };

    calculateTotalAmounts();
  }, [state.purchase_requisition_details, estimatedValueFromApi]);

  useEffect(() => {
    const { purchase_requisition_details } = state;
    let shouldEnable = false;
    for (let detail of purchase_requisition_details) {
      for (let site of detail.site_wise) {
        if (site.quantity && Number(site.quantity) > 0) {
          shouldEnable = true;
          break;
        }
      }
      if (shouldEnable) break;
    }
    setDisabled(!shouldEnable);
  }, [state.purchase_requisition_details]);

  const getSubCategory = async () => {
    const form_data = new FormData();
    form_data.append("site_ids", siteIds.join(","));

    try {
      setLoading(true);
      if (!prId) {
        const apiResponse = await postRequest(
          `company-material-code/${categoryId}/get-pr-material-by-checked/`,
          form_data,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (apiResponse.data.success && apiResponse.data.data) {
          setEstimatedValueFromApi((estimatedValueFromApi) => [
            ...estimatedValueFromApi,
            ...apiResponse.data.data.map((item) => ({
              id: item.material_type,
              value: item.estimated_price,
            })),
          ]);
          const rawData = apiResponse.data.data;
          const materialMap = {};

          rawData.forEach((item) => {
            const materialKey = item.material_type;

            if (!materialMap[materialKey]) {
              materialMap[materialKey] = {
                material_type: item.material_type_name,
                material_type_id: item.material_type,
                unit_of_measurement: item.unit_of_measurement,
                unit_of_measurement_name: item.unit_of_measurement_name,
                estimated_price: item.estimated_price,
                sub_category: item.sub_category,
                remark: null,
                total_quantity: 0,
                total_quantity_by_kg: 0,
                site_wise: [],
                company_material_id: item.company_material_id,
                smart_buyer_code: item.smart_buyer_code,
                gst_rate: item.gst_rate,
                available_sites: new Set(),
                sand_conversion: item.sand_conversion,
                category: item.category_tree_name,
              };
            }

            materialMap[materialKey].available_sites.add(item.site_id);

            const existingSite = materialMap[materialKey].site_wise.find(
              (site) => site.site_name === item.site_id
            );

            if (!existingSite && item.material_type_name.includes("Sand")) {
              materialMap[materialKey].site_wise.push({
                site_name: item.site_id,
                quantity: null,
                site_name_display: item.site_name,
                is_available: true,
                sand_conversion: item.sand_conversion,
              });
            } else {
              materialMap[materialKey].site_wise.push({
                site_name: item.site_id,
                quantity: null,
                site_name_display: item.site_name,
                is_available: true,
              });
            }
          });

          const purchaseRequisitionDetails = Object.values(materialMap);

          setState((prevState) => ({
            ...prevState,
            category_tree: categoryId,
            purchase_requisition_details: purchaseRequisitionDetails,
          }));

          const uniqueSubCategories = Array.from(
            new Set(purchaseRequisitionDetails.map((item) => item.sub_category))
          );

          setSubCategoryList(uniqueSubCategories);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getUnitOfMeasurement = async () => {
    try {
      const response = await getRequest(
        `get-category-by-uom/?category_tree=${categoryId}`
      );
      const filteredData = response.data.results.data.filter(
        (item) => item.unit_of_measurement !== "KG"
      );
      setPincodeList(filteredData);
    } catch (error) {
      console.error("Error fetching unit of measurement:", error);
    }
  };

  const getSiteName = async () => {
    try {
      const apiResponse = await getRequest(
        `delivery-address/${CompanyID}/address-by-company/`
      );
      if (apiResponse.data.data) {
        if (userSelectedSiteList.userSelectedSiteList.length === 0) {
          setSiteName(apiResponse.data.data);
        }
      } else {
        ErrorNotificationMsg(t("error_in_company_adress_list"));
      }
    } catch (error) {
      console.error("Error fetching site names:", error);
    }
  };

  const getCurrency = async () => {
    try {
      const response = await getRequest(
        "business-settings/get-currency-by-business-setting/"
      );
      setCurrencySymbol(response.data.data);
    } catch (error) {
      console.error("Error fetching currency:", error);
    }
  };

  const handleRemarkChange = (e, id, index) => {
    const { value } = e.target;
    setState((prevState) => ({
      ...prevState,
      purchase_requisition_details: prevState.purchase_requisition_details.map(
        (detail) => {
          if (detail.material_type === id) {
            return { ...detail, remark: value };
          }
          return detail;
        }
      ),
    }));
  };

  // const handleInputChange = (e, id, index, siteIDD) => {
  //   const { value } = e.target;
  //   setState((prevState) => {
  //     const newState = { ...prevState };
  //     newState.purchase_requisition_details = prevState.purchase_requisition_details.map(
  //       (detail) => {
  //         if (detail.material_type_id === id) {
  //           const updatedDetail = {
  //             ...detail,
  //             site_wise: detail.site_wise.map((site, idx) => {
  //               if (site.site_name === siteIDD) {
  //                 return { ...site, quantity: value };
  //               }
  //               return site;
  //             }),
  //           };

  //           updatedDetail.total_quantity = updatedDetail.site_wise.reduce(
  //             (acc, site) => acc + (parseFloat(site.quantity) || 0),
  //             0
  //           );
  //           console.log("updatedDetailqq", updatedDetail);

  //           updatedDetail.total_quantity_by_kg = updatedDetail.site_wise.reduce(
  //             (acc, site) => {
  //               if (updatedDetail.unit_of_measurement_name === "Cum.") {
  //                 return acc + (parseFloat(site.quantity) * site.sand_conversion * 1000 || 0);
  //               } else if (updatedDetail.unit_of_measurement_name === "MT") {
  //                 return acc + (parseFloat(site.quantity) * 1000 || 0);
  //               } else if (updatedDetail.unit_of_measurement_name === "Bag") {
  //                 return acc + (parseFloat(site.quantity) * 50 || 0);
  //               } else if (updatedDetail.unit_of_measurement_name === "Cubic Meter") {
  //                 return acc + 0;ś
  //               } else {
  //                 return acc + (parseFloat(site.quantity) || 0);
  //               }
  //             },
  //             0
  //           );

  //           return updatedDetail;
  //         }
  //         return detail;
  //       }
  //     );
  //     return newState;
  //   });
  // };

  const handleInputChange = (e, id, index, siteIDD) => {
    const { value } = e.target;

    setState((prevState) => {
      const newState = { ...prevState };

      newState.purchase_requisition_details =
        prevState.purchase_requisition_details.map((detail) => {
          if (detail.material_type_id === id) {
            const updatedDetail = {
              ...detail,
              site_wise: detail.site_wise.map((site) => {
                if (site.site_name === siteIDD) {
                  return { ...site, quantity: value };
                }
                return site;
              }),
            };

            updatedDetail.total_quantity = updatedDetail.site_wise.reduce(
              (acc, site) => acc + (parseFloat(site.quantity) || 0),
              0
            );

            const isEarthingStrip =
              updatedDetail.sub_category?.includes("Earthing");

            let widthMm = 0,
              thicknessMm = 0,
              density = 0;

            if (isEarthingStrip) {
              const match = updatedDetail.material_type.match(
                /(\d+)\s*x\s*(\d+)\s*-\s*(\w+)/i
              );

              if (match) {
                widthMm = parseFloat(match[1]);
                thicknessMm = parseFloat(match[2]);
                const metal = match[3].toUpperCase();

                density = metal === "GI" ? 7.85 : metal === "CU" ? 8.96 : 0;
              }
            }

            const getKgPerMeter = () => {
              if (!isEarthingStrip) return 0;
              return (widthMm * thicknessMm * density) / 1000;
            };

            const kgPerMeter = getKgPerMeter();

            updatedDetail.total_quantity_by_kg = updatedDetail.site_wise.reduce(
              (acc, site) => {
                const qty = parseFloat(site.quantity) || 0;

                if (isEarthingStrip) {
                  return acc + qty * kgPerMeter;
                }

                if (
                  updatedDetail.material_type?.includes("Sand") &&
                  updatedDetail.unit_of_measurement_name === "Cum."
                ) {
                  return acc + qty * updatedDetail.sand_conversion * 1000;
                }

                if (
                  updatedDetail.material_type?.includes("Sand") &&
                  updatedDetail.unit_of_measurement_name === "MT"
                ) {
                  return acc + qty * 1000;
                }

                if (
                  (updatedDetail.material_type?.includes("Aggregate") ||
                    updatedDetail.material_type?.includes("Dust")) &&
                  updatedDetail.unit_of_measurement_name === "Cum."
                ) {
                  return acc + qty * updatedDetail.aggregate_conversion * 1000;
                }
                if (
                  (updatedDetail.material_type?.includes("Aggregate") ||
                    updatedDetail.material_type?.includes("Dust")) &&
                  updatedDetail.unit_of_measurement_name === "MT"
                ) {
                  return acc + qty * 1000;
                }

                if (updatedDetail.unit_of_measurement_name === "MT") {
                  return acc + qty * 1000;
                }

                if (updatedDetail.unit_of_measurement_name === "Bag") {
                  return acc + qty * 50;
                }
                return acc + qty;
              },
              0
            );

            return updatedDetail;
          }

          return detail;
        });

      return newState;
    });
  };

  const handleUOMChange = (value, materialId, unitOfMeasurement) => {
    setState((prevState) => ({
      ...prevState,
      purchase_requisition_details: prevState.purchase_requisition_details.map(
        (item) => {
          if (item.material_type === materialId) {
            if (unitOfMeasurement === "Cum.") {
              return {
                ...item,
                unit_of_measurement: value,
                unit_of_measurement_name: unitOfMeasurement,
                total_quantity_by_kg:
                  item.total_quantity * item.sand_conversion * 1000,
              };
            } else if (unitOfMeasurement === "MT") {
              return {
                ...item,
                unit_of_measurement: value,
                unit_of_measurement_name: unitOfMeasurement,
                total_quantity_by_kg: item.total_quantity * 1000,
              };
            } else if (unitOfMeasurement === "Bag") {
              return {
                ...item,
                unit_of_measurement: value,
                unit_of_measurement_name: unitOfMeasurement,
                total_quantity_by_kg: item.total_quantity * 50,
              };
            } else {
              return {
                ...item,
                unit_of_measurement: value,
                unit_of_measurement_name: unitOfMeasurement,
                total_quantity_by_kg: item.total_quantity,
              };
            }
          }
          return item;
        }
      ),
    }));
  };

  // const calculateGrandTotal = () => {
  //   return state.purchase_requisition_details.reduce((total, detail) => {
  //     let quantitywithCubic = 0;
  //     if (detail.unit_of_measurement_name === "Cubic Meter") {
  //       quantitywithCubic += detail.total_quantity;
  //     } else {
  //       quantitywithCubic += detail.total_quantity_by_kg;
  //     }
  //     const amount = (quantitywithCubic || 0) * (detail.estimated_price || 0);
  //     return total + amount;
  //   }, 0);
  // };

  const calculateGrandTotal = () => {
    return state.purchase_requisition_details.reduce((total, detail) => {
      const isEarthing = detail.sub_category?.includes("Earthing");

      if (isEarthing) {
        const parsed = parseEarthingStrip(detail.material_type);
        if (parsed) {
          const kgPerMeter = getKgPerMeter(
            parsed.width,
            parsed.thickness,
            parsed.material
          );
          const ratePerKg = detail.estimated_price || 0;
          const ratePerMeter = kgPerMeter * ratePerKg;
          const finalRatePerMeter = ratePerMeter * 1.1;

          const totalMeters = detail.total_quantity || 0;
          return (
            total +
            totalMeters *
              finalRatePerMeter.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })
          );
        }
        return total;
      }

      if (
        detail.unit_of_measurement_name === "Cubic Meter" ||
        detail.sub_category === "RMC"
      ) {
        return (
          total + (detail.total_quantity || 0) * (detail.estimated_price || 0)
        );
      }

      return (
        total +
        (detail.total_quantity_by_kg || 0) * (detail.estimated_price || 0)
      );
    }, 0);
  };

  const subCategoryTables = subCategoryList.map((subCategory, index) => {
    let customSubcategory =
      subCategory === "SPECIAL CEMENT" ? "Special Cement" : subCategory;

    const subCategoryItems = state.purchase_requisition_details.filter(
      (item) => item.sub_category === subCategory
    );
    console.log("sscustomSubcategory", customSubcategory);

    const subCategoryColumns = [
      {
        title: index + 1,
        dataIndex: "Sr No",
        key: "1",
        width: 50,
        render: (text, record, ind) => `${index + 1}.${ind + 1}`,
      },
      {
        title: (
          <span>
            {customSubcategory}
            {customSubcategory.toLowerCase().includes("gi") && (
              <Tooltip
                title="Hot Dip GI Earthing Strip (80 to 100 micron)"
                // color="#094E8F"
                placement="top"
              >
                <InfoCircleFilled
                  style={{
                    color: "#1890ff",
                    marginLeft: 8,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </span>
        ),
        dataIndex: "material_type",
        key: `material_type_${index}`,
        width: 180,
        render: (text, record) => {
          return <span>{text}</span>;
        },
      },
      ...(index === 0
        ? siteName.map((site, siteIndex) => ({
            title: site.site_name,
            dataIndex: `site_name_${siteIndex}`,
            key: `${site.site_name}_${subCategory}_${siteIndex}`,
            width: 150,
            render: (text, record) => {
              const isAvailable = record.available_sites?.has(site.id);
              if (!isAvailable) {
                return <span style={{ color: "#999" }}>N/A</span>;
              }
              const siteData = record.site_wise.find(
                (s) => s.site_name === site.id
              );
              return (
                <Input
                  type="text"
                  min={0}
                  maxLength={5}
                  placeholder="Enter Qty"
                  style={{ width: "80%" }}
                  value={siteData?.quantity || ""}
                  onChange={(e) =>
                    handleInputChange(
                      e,
                      record.material_type_id,
                      siteIndex,
                      site.id
                    )
                  }
                  onKeyPress={(e) => {
                    if (!/^\d$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              );
            },
          }))
        : siteName.map((site, siteIndex) => ({
            key: `${site.site_name}_${subCategory}_${siteIndex}`,
            width: 150,
            render: (text, record) => {
              const isAvailable = record.available_sites?.has(site.id);
              if (!isAvailable) {
                return <span style={{ color: "#999" }}>N/A</span>;
              }
              const siteData = record.site_wise.find(
                (s) => s.site_name === site.id
              );
              return (
                <Input
                  type="text"
                  min={0}
                  maxLength={5}
                  placeholder="Enter Qty"
                  style={{ width: "80%" }}
                  value={siteData?.quantity || ""}
                  onChange={(e) =>
                    handleInputChange(
                      e,
                      record.material_type_id,
                      siteIndex,
                      site.id
                    )
                  }
                  onKeyPress={(e) => {
                    if (!/^\d$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              );
            },
          }))),
      {
        title:
          index === 0 ? (
            <Tooltip
              color="#094E8F"
              placement="top"
              title={
                <span style={{ color: "#fff" }}>
                  Preferred Unit of measurement means Your company buys material
                  generally in this unit of measurement
                </span>
              }
            >
              Preferred UoM.
            </Tooltip>
          ) : (
            ""
          ),
        dataIndex: "unit_of_measurement",
        key: "unit_of_measurement",
        width: 120,
        render: (text, record) => {
          console.log("recddord", record);

          const defaultValue = record.unit_of_measurement;

          if (record.sub_category === "RMC") {
            return (
              <Select
                style={{ width: "100%" }}
                defaultValue={4}
                suffixIcon={null}
                open={false}
                onChange={(value) => {
                  const selectedOption = pincodeList.find(
                    (option) => option.id === value
                  );
                  handleUOMChange(
                    value,
                    record.material_type,
                    selectedOption.unit_of_measurement
                  );
                }}
              >
                {pincodeList.map((option) => (
                  <Option key={option.id} value={option.id}>
                    <span style={{ color: "black", fontSize: "12px" }}>
                      {option.id === 4
                        ? "Cu. Mtr."
                        : option.unit_of_measurement}
                    </span>
                  </Option>
                ))}
              </Select>
            );
          } else if (record.category === "Earthing") {
            return (
              <div key={index}>
                <Select
                  style={{ width: "80%" }}
                  defaultValue={"Meter"}
                  suffixIcon={null}
                  open={false}
                />
              </div>
            );
          } else {
            return (
              <Select
                style={{ width: "90%" }}
                defaultValue={defaultValue}
                onChange={(value) => {
                  const selectedOption = pincodeList.find(
                    (option) => option.id === value
                  );
                  handleUOMChange(
                    value,
                    record.material_type,
                    selectedOption.unit_of_measurement
                  );
                }}
              >
                {pincodeList
                  .filter((i) => i.unit_of_measurement !== "Cubic Meter")
                  .map((option) => (
                    <Option key={option.id} value={option.id}>
                      {option.unit_of_measurement}
                    </Option>
                  ))}
              </Select>
            );
          }
        },
      },
      selectedCategoryName !== "Earthing" && {
        title: (
          <Tooltip
            color="#094E8F"
            placement="top"
            title={<span style={{ color: "#fff" }}>Quantity In Kg</span>}
          >
            Qty. In Kg
          </Tooltip>
        ),
        width: 100,
        dataIndex: "total_quantity_by_kg",
        key: "total_quantity_by_kg",
        align: "right",
        render: (text, record) => {
          if (record.sub_category === "RMC") {
            return <span>-</span>;
          }
          return (
            <span>
              {record.total_quantity_by_kg?.toLocaleString("en-IN", {
                maximumFractionDigits: 2,
              }) || 0}
            </span>
          );
        },
      },
      {
        title: (
          <Tooltip
            title="Reference price of estimated unit rate comes from material master data"
            color="#094E8F"
            placement="top"
          >
            <span>Est. Unit rate</span>
          </Tooltip>
        ),
        dataIndex: "estimated_price",
        width: 160,
        align: "right",
        render: (text, record) => {
          const isEarthing = record.sub_category?.includes("Earthing");

          if (isEarthing) {
            const parsed = parseEarthingStrip(record.material_type);
            if (parsed) {
              const kgPerMeter = getKgPerMeter(
                parsed.width,
                parsed.thickness,
                parsed.material
              );
              const ratePerKg = record.estimated_price || 0;
              const ratePerMeter = kgPerMeter * ratePerKg;
              const finalRatePerMeter = ratePerMeter * 1.1; // +10% profit

              return (
                <span style={{ color: "#d4380d", fontWeight: 500 }}>
                  ₹ {Math.round(finalRatePerMeter).toLocaleString("en-IN")} / m
                </span>
              );
            }
          }

          // Default for non-earthing
          return (
            <span>
              ₹{" "}
              {record.estimated_price?.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              }) || 0}{" "}
              /MT
            </span>
          );
        },
      },
      {
        title: (
          <Tooltip
            color="#094E8F"
            placement="top"
            title={<span style={{ color: "#fff" }}>Estimated Amount</span>}
          >
            Est. Amount
          </Tooltip>
        ),
        dataIndex: "estimated_amount",
        width: 160,
        align: "right",
        render: (text, record) => {
          const isEarthing = record.sub_category?.includes("Earthing");

          if (isEarthing) {
            const parsed = parseEarthingStrip(record.material_type);
            if (!parsed) return <span>₹ 0</span>;

            const kgPerMeter = getKgPerMeter(
              parsed.width,
              parsed.thickness,
              parsed.material
            );
            const ratePerKg = record.estimated_price || 0;
            const ratePerMeter = kgPerMeter * ratePerKg;
            const finalRatePerMeter = ratePerMeter * 1.1;

            const totalMeters = record.total_quantity || 0;
            const totalAmount =
              totalMeters *
              finalRatePerMeter.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              });

            return (
              <span>₹ {Math.round(totalAmount).toLocaleString("en-IN")}</span>
            );
          }

          // Existing logic for non-earthing (RMC, Sand, etc.)
          if (record.sub_category === "RMC") {
            const totalQty = record.site_wise.reduce(
              (acc, site) => acc + (parseFloat(site.quantity) || 0),
              0
            );
            return (
              <span>
                ₹{" "}
                {(totalQty * (record.estimated_price || 0)).toLocaleString(
                  "en-IN",
                  {
                    maximumFractionDigits: 0,
                  }
                )}
              </span>
            );
          }

          // For all other materials (Sand, Cement, etc.)
          const totalKg = record.total_quantity_by_kg || 0;
          const amount = totalKg * (record.estimated_price || 0);

          return (
            <span>
              ₹ {amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </span>
          );
        },
      },
      {
        title: "Remarks",
        dataIndex: "company_material_code.company_material_name",
        key: "company_material_name",
        width: 170,
        render: (text, record) => (
          <Input
            type="text"
            placeholder="Enter Remarks"
            style={{ width: "150px" }}
            value={record.remark || ""}
            onChange={(e) => handleRemarkChange(e, record.material_type, index)}
          />
        ),
      },
    ].filter(Boolean);

    const theadColumns = <Table columns={subCategoryColumns} />;
    return (
      <>
        {" "}
        <Collapse
          defaultActiveKey={["4"]}
          className="scroll5"
          key={subCategory}
        >
          <Panel header={theadColumns} key="4">
            <Table
              key={subCategory}
              columns={subCategoryColumns}
              dataSource={subCategoryItems}
              pagination={false}
              rowKey={(record) => record.material_type_id}
            />
          </Panel>
        </Collapse>
      </>
    );
  });

  const handleSubmit = async () => {
    try {
      setBtnLoading(true);
      const unitMultipliers = { 1: 1000, 3: 50 };
      const payload = {
        company: state.company,
        category_tree: state.category_tree,
        total_amount: state.total_amount,
        pr_material_details: state.purchase_requisition_details
          .map((detail) => {
            const prSiteDetails = detail.site_wise
              .map((site) => {
                let totalQuantity = site.quantity;
                let uom = detail.unit_of_measurement_name;
                let kgAmount = 0;
                if (uom === "Cum.") {
                  kgAmount = site.quantity * site.sand_conversion * 1000;
                } else if (uom === "MT") {
                  kgAmount = site.quantity * 1000;
                } else if (uom === "Bag") {
                  kgAmount = site.quantity * 50;
                }
                const multiplier =
                  unitMultipliers[detail.unit_of_measurement] || 1;
                totalQuantity = site.quantity * multiplier;
                return {
                  site_name: site.site_name,
                  total_quantity: site.quantity,
                  total_quantity_by_kg: kgAmount,
                };
              })
              .filter((site) => site.total_quantity > 0);
            return prSiteDetails.length > 0
              ? {
                  material_type_by_company: detail.material_type_id,
                  unit_of_measurement: detail.unit_of_measurement,
                  remark: detail.remark,
                  pr_site_details: prSiteDetails,
                }
              : null;
          })
          .filter((material) => material !== null),
      };

      let response;
      if (!prId) {
        response = await postRequest(
          "purchase-requisition-by-material/",
          payload
        );
      } else {
        response = await postRequest(
          "purchase-requisition-by-material/",
          payload
        );
      }

      if (response.data.success) {
        SuccessNotificationMsg(response.data.message);
        setState(initialState);
        navigate("/purchase-requisition-history");
      }
    } catch (error) {
      console.error("Error submitting PR:", error);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      {subCategoryTables}
      <div style={{ margin: "24px 0" }}>
        <Table
          showHeader={false}
          pagination={false}
          dataSource={[{}]}
          columns={[
            {
              title: "Total Est. Amount",
              align: "right",
              width: "100%",
              render: () => (
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    marginTop: "5px",
                  }}
                >
                  Estimated Total Price
                </div>
              ),
            },
            {
              title: "Total Est. Amount",
              align: "right",
              render: () => (
                <div
                  className="totalCityOverallSitee5"
                  style={{ width: "100%", textAlign: "end" }}
                >
                  {currencySymbol}{" "}
                  {calculateGrandTotal().toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </div>
              ),
            },
          ]}
        />
      </div>
      <Form>
        <div className="btnwrp" style={{ paddingTop: "16px" }}>
          <ButtonField
            className="CancelGradiantBtn"
            type="primary"
            htmlType="Cancel"
            label="Cancel"
            onClick={() => navigate("/purchase-requisition-history")}
          />
          <ButtonField
            className="SaveGradiantBtn"
            type="primary"
            htmlType="Send PR for Approval"
            label="Send PR for Approval"
            loading={btnLoading}
            onClick={handleSubmit}
            disabled={disabled}
          />
        </div>
      </Form>
    </Spin>
  );
};

NewAddPRMaterial.propTypes = {
  userSelectedSiteList: PropTypes.shape({
    userSelectedSiteList: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        site_name: PropTypes.string.isRequired,
        city_name: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  selectedCategoryName: PropTypes.object,
};

export default NewAddPRMaterial;
